import React, {createContext, useContext, useReducer, useCallback} from 'react';
import PropTypes from 'prop-types';
import {SCREENS, mapPageStateToScreen} from '../constants/renewalClick';

const RenewalClickContext = createContext();

const initialState = {
  shortLinkKey: null,
  currentScreen: SCREENS.LOADING,
  loading: false,
  error: null,
  pageState: null,
  customer: {},
  vehicle: {},
  policy: {},
  pricing: {},
  meta: {},
  contact: {},
  authMethods: [],
  maskedPhone: '',
  customerName: '',
  opportunityId: null,
  sameYearConfirmed: false,
  paymentProcessing: false,
  paymentError: null,
  signingCompleted: false,
};

function reducer(state, action) {
  switch (action.type) {
  case 'SET_PAGE_DATA':
    return {
      ...state,
      shortLinkKey: action.payload.shortLinkKey,
      pageState: action.payload.pageState,
      customer: action.payload.customer || {},
      vehicle: action.payload.vehicle || {},
      policy: action.payload.policy || {},
      pricing: action.payload.pricing || {},
      meta: action.payload.meta || {},
      contact: action.payload.contact || {},
      authMethods: action.payload.authMethods || [],
      maskedPhone: action.payload.maskedPhone || '',
      customerName: action.payload.customerName || '',
      opportunityId: action.payload.opportunityId || null,
      currentScreen: mapPageStateToScreen(action.payload.pageState),
      loading: false,
    };
  case 'SET_SCREEN':
    return {...state, currentScreen: action.payload};
  case 'SET_LOADING':
    return {...state, loading: action.payload};
  case 'SET_ERROR':
    return {...state, error: action.payload, currentScreen: SCREENS.ERROR};
  case 'SET_SAME_YEAR_CONFIRMED':
    return {...state, sameYearConfirmed: action.payload};
  case 'SET_PAYMENT_PROCESSING':
    return {...state, paymentProcessing: action.payload};
  case 'SET_PAYMENT_ERROR':
    return {...state, paymentError: action.payload};
  case 'SET_SIGNING_COMPLETED':
    return {...state, signingCompleted: action.payload, currentScreen: SCREENS.COMPLETION};
  default:
    return state;
  }
}

export function RenewalClickProvider({children}) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setPageData = useCallback((shortLinkKey, data) => {
    dispatch({type: 'SET_PAGE_DATA', payload: {shortLinkKey, ...data}});
  }, []);

  const setScreen = useCallback((screen) => {
    dispatch({type: 'SET_SCREEN', payload: screen});
  }, []);

  const setLoading = useCallback((loading) => {
    dispatch({type: 'SET_LOADING', payload: loading});
  }, []);

  const setError = useCallback((error) => {
    dispatch({type: 'SET_ERROR', payload: error});
  }, []);

  const setSameYearConfirmed = useCallback((confirmed) => {
    dispatch({type: 'SET_SAME_YEAR_CONFIRMED', payload: confirmed});
  }, []);

  const setPaymentProcessing = useCallback((processing) => {
    dispatch({type: 'SET_PAYMENT_PROCESSING', payload: processing});
  }, []);

  const setPaymentError = useCallback((error) => {
    dispatch({type: 'SET_PAYMENT_ERROR', payload: error});
  }, []);

  const setSigningCompleted = useCallback((completed) => {
    dispatch({type: 'SET_SIGNING_COMPLETED', payload: completed});
  }, []);

  const value = {
    ...state,
    setPageData,
    setScreen,
    setLoading,
    setError,
    setSameYearConfirmed,
    setPaymentProcessing,
    setPaymentError,
    setSigningCompleted,
  };

  return (
    <RenewalClickContext.Provider value={value}>
      {children}
    </RenewalClickContext.Provider>
  );
}

RenewalClickProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useRenewalClick() {
  const context = useContext(RenewalClickContext);
  if (!context) {
    throw new Error('useRenewalClick must be used within RenewalClickProvider');
  }
  return context;
}
